import { portalPaths } from '@provision/portal-routes';
import { useCallback } from 'react';
import { isFunction, omitBy } from 'remeda';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import {
  CustomView,
  CustomViewFormModel,
  CustomViewSorting,
} from 'src/graphql/entities/custom-view';
import { useGlobalStore } from 'src/store';
import { useNotifications } from 'src/utils/hooks';
import { useNavigateRoute } from 'src/utils/routing';

type CopyCustomViewValues = Pick<
  CustomView,
  | 'name'
  | 'description'
  | 'entityName'
  | 'formConditions'
  | 'columns'
  | 'sort'
  | 'icon'
  | 'isPrivate'
>;

export const useCopyCustomView = () => {
  const { navigate } = useNavigateRoute();
  const { showErrorAlert } = useNotifications();
  const partnerId = useGlobalStore.use.selectedPartnerId() ?? PLATFORM_PARTNER_ID;

  const goToCopyView = useCallback(
    (view: CopyCustomViewValues) => {
      try {
        navigate(portalPaths.customView.action(view.entityName, 'new'), {
          state: {
            initialValues: {
              name: `${view.name} (copy)`,
              partnerId,
              description: view.description,
              entityName: view.entityName,
              conditions: {
                ...view.formConditions,
                rules: view.formConditions.rules.map((rule) => ({
                  ...rule,
                  // functions can't be passed via router state
                  field: omitBy(rule.field, isFunction),
                })),
              },
              // functions can't be passed via router state
              columns: view.columns?.map((i) => omitBy(i, isFunction)),
              sort: view.sort as CustomViewSorting & { id: string; value: string },
              icon: view.icon,
              isPrivate: view.isPrivate,
            } as CustomViewFormModel,
          },
        });
      } catch (error) {
        console.error(error);
        showErrorAlert('Something went wrong, please try again later');
      }
    },
    [navigate, partnerId, showErrorAlert],
  );

  return { goToCopyView };
};
